import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query } from 'graphql/generated/graphqlTypes';
import { GET_DOORS_FOR_TENANT, GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useMemo } from 'react';
import { stitchDoorLocations } from 'v2/utils/lock';
import { TranslatedHookProps } from '../hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;

const useDevices = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const {
    loading: doorsLoading,
    error: doorsError,
    data: doors
  } = useQuery(GET_DOORS_FOR_TENANT_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const {
    loading: locationsLoading,
    error: locationsError,
    data: locations
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const devicesLoading = (doorsLoading && !doors) || (locationsLoading && !locations);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingDevices = !devicesLoading && ((!!doors && doorsLoading) || (!!locations && locationsLoading));

  useRegisterBackgroundRefetch(isRefetchingDevices);

  const devices = useMemo(
    () => stitchDoorLocations(doors?.getDoorsForTenant ?? [], locations?.getLocationsForTenant ?? []),
    [doors, locations]
  );

  return {
    devices,
    devicesLoading,
    isRefetchingDevices,
    lockGroups: locations?.getLocationsForTenant || [],
    lockGroupsLoading: locationsLoading && !locations
  };
};

export default useDevices;
